import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router'
import App from './App.vue'
import vPermission from './directives/permission'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import './assets/styles/main.scss'
import { BUILD_ID, DISPLAY_VERSION, FRONTEND_STATE_SCHEMA, SOFTWARE_REGISTRATION, clearLegacyFrontendState } from './buildInfo'

const systemRegistration = SOFTWARE_REGISTRATION.system
if (systemRegistration) document.title = `${systemRegistration.name} ${DISPLAY_VERSION}`


async function runCacheHealthCheck(): Promise<void> {
  const key = '__zhilin_cache_check__'
  try {
    const seen = sessionStorage.getItem(key)
    sessionStorage.setItem(key, BUILD_ID)
    if (seen && seen !== BUILD_ID) clearLegacyFrontendState()
  } catch { /* storage may be unavailable */ }
  try {
    const response = await fetch('/v2/version.json?build=' + encodeURIComponent(BUILD_ID), {
      cache: 'no-store', credentials: 'same-origin',
      headers: { 'Cache-Control': 'no-cache', 'X-Frontend-Build': BUILD_ID },
    })
    if (!response.ok) return
    const remote = await response.json() as { build?: string; version?: string }
    const remoteBuild = String(remote.build || remote.version || '')
    if (remoteBuild && remoteBuild !== BUILD_ID) {
      window.dispatchEvent(new CustomEvent('frontend:update-available', {
        detail: { current: BUILD_ID, remote: remoteBuild, schema: FRONTEND_STATE_SCHEMA },
      }))
      console.warn('[frontend] newer build available', { current: BUILD_ID, remote: remoteBuild })
    }
  } catch { /* version check must never block app startup */ }
}
void runCacheHealthCheck()
import { installTeacherReads } from '@/utils/teacherReads'
import apiClient from '@/api'
import { queryScope } from '@/utils/queryScope'

const app = createApp(App)
app.use(createPinia())
installTeacherReads(apiClient, queryScope)
app.use(router)
app.use(ElementPlus)

// ─── 注册全局指令 ────────────────────────────────
app.directive('permission', vPermission)
app.mount('#app')

// ─── PWA Service Worker 注册 ───────────────────
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    if (import.meta.env.DEV) {
      console.log('[PWA] 开发模式下不注册 Service Worker')
    }
  })
}

// ─── 全局错误处理 ────────────────────────────────
// ★ Rule 4: 全局错误处理 (DEV 下不吞初始化期异常)
app.config.errorHandler = (err: unknown, _instance: unknown, info: string) => {
  console.error(`[Vue Error] ${info}:`, err)
  // DEV: 原始 stack 直接输出, 便于定位 getActivePinia / Store 残缺 等致命错误
  if (import.meta.env.DEV && err instanceof Error && err.stack) {
    console.error(err.stack)
  }
  // 生产: 轻量 toast 提示
  const msg = err instanceof Error ? err.message : String(err)
  if (msg.includes("ResizeObserver") || msg.includes("Script error")) return
  try {
    const showToast = (window as any).__toast__
    if (showToast) showToast("操作异常: " + (msg.slice(0, 80) || "未知错误") + "，请刷新重试", "warn")
  } catch {
    if (import.meta.env.DEV) console.warn("[errorHandler] Toast unavailable")
  }
}

// ─── 未处理的 Promise 异常 ──────────────────────
window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
  if (import.meta.env.DEV) {
    console.error("[Unhandled Rejection]:", event.reason)
    if (event.reason instanceof Error) console.error(event.reason.stack)
  }
  const reason = event.reason as { response?: { status?: number }; message?: string } | undefined
  // Session expiry is decided by the Axios interceptor. Redirecting here
  // races a successful cookie refresh when multiple views load together.
  if (reason?.response?.status === 401 || (reason as any)?.code === 'ERR_CANCELED') return
  try {
    const showToast = (window as any).__toast__
    if (showToast) showToast("请求异常: " + ((reason?.message || "网络错误").slice(0, 80)), "warn")
  } catch { /* Pinia not ready */ }
})

